/**
 * An invitation you can paste anywhere.
 *
 * Not everybody you want in a party has an account you know the name of -
 * they are in a Discord, or a group chat, or standing next to you. A link
 * covers all three.
 *
 * Every link carries its own end: a time, a number of uses, or both. The way
 * invite links go wrong is that they still work three months later, so there
 * is no option here for one that never expires.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { copyText } from '@/lib/clipboard';
import { Icon } from '@/icons/Icon';
import { Button, useToast } from './ui';

type Link = {
  id: string;
  token: string;
  url: string;
  expiresAt: number | null;
  maxUses: number;
  uses: number;
  createdAt: number;
};

const SPANS = [
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 60 * 6, label: '6 hours' },
  { minutes: 60 * 24, label: '1 day' },
  { minutes: 60 * 24 * 7, label: '7 days' }
] as const;

const USES = [
  { n: 0, label: 'Any number' },
  { n: 1, label: 'Once' },
  { n: 5, label: '5 times' },
  { n: 10, label: '10 times' }
] as const;

/** "expires in 42m", or what is left of its uses. */
function lifeText(link: Link): string {
  const bits: string[] = [];
  if (link.expiresAt) {
    const mins = Math.max(0, Math.round((link.expiresAt - Date.now()) / 60000));
    bits.push(mins < 60 ? mins + 'm left' : mins < 60 * 48 ? Math.round(mins / 60) + 'h left' : Math.round(mins / 1440) + 'd left');
  }
  if (link.maxUses) bits.push(link.uses + '/' + link.maxUses + ' used');
  else if (link.uses) bits.push(link.uses + ' used');
  return bits.join(' · ') || 'open';
}

export function InviteLinks({ partyId, canManage }: { partyId: string; canManage: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [minutes, setMinutes] = useState<number>(60);
  const [maxUses, setMaxUses] = useState<number>(0);

  const links = useQuery({
    queryKey: ['party-links', partyId],
    queryFn: () => api.get<{ links: Link[] }>('/api/parties/' + partyId + '/links')
  });

  const make = useMutation({
    mutationFn: () => api.post<{ link: Link }>('/api/parties/' + partyId + '/links', { minutes, maxUses }),
    onSuccess: async (res) => {
      void queryClient.invalidateQueries({ queryKey: ['party-links', partyId] });
      const ok = await copyText(res.link.url);
      toast(ok ? 'Link copied - paste it anywhere' : 'Link made', ok ? 'good' : 'good');
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const drop = useMutation({
    mutationFn: (id: string) => api.del('/api/parties/' + partyId + '/links/' + id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['party-links', partyId] });
      toast('That link stops working now', 'good');
    },
    onError: (err: Error) => toast(err.message, 'bad')
  });

  const list = links.data?.links ?? [];

  return (
    <section className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-inset p-3">
      <div className="flex items-center gap-2">
        <Icon name="link" size={15} className="flex-none text-accent" />
        <h3 className="m-0 flex-1 text-[13.5px] font-bold text-ink">Invite by link</h3>
      </div>

      {list.length ? (
        <ul className="m-0 mt-2.5 flex list-none flex-col gap-1.5 p-0">
          {list.map((link) => (
            <li key={link.id} className="flex items-center gap-2 rounded-[calc(10px*var(--radius-mul,1))] bg-surface-1 px-2.5 py-1.5">
              <button
                type="button"
                onClick={() => void copyText(link.url).then((ok) => toast(ok ? 'Link copied' : 'Could not copy', ok ? 'good' : 'bad'))}
                className="group flex min-w-0 flex-1 items-center gap-2 text-left outline-none"
              >
                <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-dim">{link.url.replace(/^https?:\/\//, '')}</span>
                <Icon name="copy" size={13} className="flex-none text-ghost transition-colors group-hover:text-accent" />
              </button>
              <span className="flex-none text-[11px] text-ghost">{lifeText(link)}</span>
              {canManage ? (
                <button
                  type="button"
                  aria-label="Stop this link working"
                  onClick={() => drop.mutate(link.id)}
                  className="grid h-7 w-7 flex-none place-items-center rounded-full text-ghost outline-none transition-colors hover:bg-surface-3 hover:text-bad"
                >
                  <Icon name="close" size={13} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 mt-2 text-[12.5px] text-mute">
          {canManage ? 'Make one and paste it wherever your people are.' : 'The host has not made a link for this party.'}
        </p>
      )}

      {canManage ? (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex flex-wrap items-center gap-1">
              <span className="mr-1 text-[11.5px] font-semibold text-mute uppercase">Lasts</span>
              {SPANS.map((s) => (
                <Chip key={s.minutes} on={minutes === s.minutes} onClick={() => setMinutes(s.minutes)}>{s.label}</Chip>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <span className="mr-1 text-[11.5px] font-semibold text-mute uppercase">Uses</span>
              {USES.map((u) => (
                <Chip key={u.n} on={maxUses === u.n} onClick={() => setMaxUses(u.n)}>{u.label}</Chip>
              ))}
            </div>
          </div>
          <Button
            size="sm"
            kind="outline"
            className="mt-2.5"
            disabled={make.isPending}
            icon={<Icon name="link" size={15} />}
            onClick={() => make.mutate()}
          >
            {make.isPending ? 'Making…' : 'Make a link and copy it'}
          </Button>
        </>
      ) : null}
    </section>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'h-7 rounded-full px-2.5 text-[12px] font-semibold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
        on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
      )}
    >
      {children}
    </button>
  );
}
